// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import style from './NotificationsMenu.scss';
import navbarStyle from '../../components/Navbar.scss';
import icons from '../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import { Default, Desktop, Mobile, Tablet } from '../../../../utils/responsive';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Helmet } from 'react-helmet';
import Loading from '../../../../elements/Loading/Loading';
import { Link } from 'react-router-dom';
import { NotificationTypeMap } from '../../../../entities/notification';
import InfiniteScroll from '../../../../elements/InfiniteScroll/InfiniteScroll';
import { socketConnect } from '../../../../utils/socket';

import type { SocketConnection } from '../../../../utils/socket';
import type { NotificationsState } from '../duck';
import type { Notification, TransactionMessage, User } from '../../../../entities';
import type { Location } from '../../../../router';
import type { OrderType } from '../../../order/duck';
import { freeShippingCap } from '../../../local-values/utils/benefits';

type Props = {
  +open: ?boolean;
  +user: User;
  +notificationsState: NotificationsState;
  +closeNotificationsMenu: () => void;
  +fetchUnseen: () => void;
  +fetchNotifications: () => void;
  +readNotification: (id: string) => void;
  +hideNotification: (id: string) => void;
  +reset: () => void;
  +newRealtimeNotification: (notification: Notification) => void;
  +location: Location;
  +fetchOrder: (orderId: string, orderType: OrderType) => void;
};

type State = {};

const messages = defineMessages({
  newMessage: {
    id: 'nav.notificationsMenu.subtitle.newMessage',
  },
  orderPaid: {
    id: 'nav.notificationsMenu.subtitle.orderPaid',
  },
  orderPurchased: {
    id: 'nav.notificationsMenu.subtitle.orderPurchased',
  },
  orderReceived: {
    id: 'nav.notificationsMenu.subtitle.orderReceived',
  },
  orderReceivedWithCancellation: {
    id: 'nav.notificationsMenu.subtitle.orderReceivedWithCancellation',
  },
  orderArrived: {
    id: 'nav.notificationsMenu.subtitle.orderArrived',
  },
  orderInQempoConfirmed: {
    id: 'nav.notificationsMenu.subtitle.orderInQempoConfirmed',
  },
  orderShippingPurchase: {
    id: 'nav.notificationsMenu.subtitle.orderShippingPurchase',
  },
  orderDelivered: {
    id: 'nav.notificationsMenu.subtitle.orderDelivered',
  },
  orderReturned: {
    id: 'nav.notificationsMenu.subtitle.orderReturned',
  },
  orderCanceled: {
    id: 'nav.notificationsMenu.subtitle.orderCanceled',
  },
  orderItemCanceled: {
    id: 'nav.notificationsMenu.subtitle.orderItemCanceled',
  },
  reservationExpired: {
    id: 'nav.notificationsMenu.subtitle.reservationExpired',
  },
  orderInQempo: {
    id: 'nav.notificationsMenu.subtitle.orderInQempo',
  },
  orderShippingTraveler: {
    id: 'nav.notificationsMenu.subtitle.orderShippingTraveler',
  },
  orderDisbursementSent: {
    id: 'nav.notificationsMenu.subtitle.orderDisbursementSent',
  },
});

export default class NotificationsMenu extends Component<Props, State> {
  socket: SocketConnection;
  socketAction: string = 'new';

  handleClickOutside(e: Event) {
    const { closeNotificationsMenu } = this.props;
    const wrap = document.querySelector(`.${style.notificationMenuWrap}`);
    const mainWrap = document.querySelector(`.${navbarStyle.notificationButton}`);
    if (wrap && mainWrap && e.target instanceof Node) {
      const target = e.target;
      if (!mainWrap.contains(target) && !wrap.contains(target)) {
        closeNotificationsMenu();
      }
    }
  }

  // Only in browser
  componentDidMount() {
    const { fetchUnseen, fetchNotifications, notificationsState, user, newRealtimeNotification } = this.props;
    if (typeof notificationsState.unseen === 'undefined')
      fetchUnseen();
    if (typeof notificationsState.notifications === 'undefined') {
      fetchNotifications();
    }
    document.addEventListener('click', this.handleClickOutside.bind(this));
    // Initialize socket
    this.socket = socketConnect('notifications', {
      id: user.id,
    });
    this.socket.on(this.socketAction, (notification: Notification) => {
      newRealtimeNotification(notification);
      this.updateCurrentView(notification);
    });
  }

  componentWillUnmount() {
    const { reset } = this.props;
    reset();
    // Ensure to remove listener if loggedout with menu open
    document.removeEventListener('click', this.handleClickOutside.bind(this));
    // Ensure to remove socket listener
    if (this.socket) {
      this.socket.off(this.socketAction);
      this.socket.close();
    }
  }

  updateCurrentView(notification: Notification) {
    const { location, fetchOrder } = this.props;
    const splitted = location.pathname.split('/');
    const orderId: string = notification.order.id;
    switch (notification.subtype) {
      //review when orders merge with transaction
      case NotificationTypeMap.PURCHASER_ORDER_PURCHASED:
      case NotificationTypeMap.PURCHASER_ORDER_PAID:
      /*if (splitted[3] === orderId) {
        fetchOrder(orderId, 'purchase');
      }
      break;*/
      case NotificationTypeMap.PURCHASER_ORDER_SHIPPING:
      case NotificationTypeMap.PURCHASER_ORDER_ARRIVED:
      case NotificationTypeMap.PURCHASER_ORDER_DELIVERED:
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED:
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED_WITH_CANCELLATIONS:
      case NotificationTypeMap.PURCHASER_ORDER_IN_QEMPO_CONFIRMED:
      case NotificationTypeMap.PURCHASER_ORDER_RETURNED:
      case NotificationTypeMap.PURCHASER_ORDER_CANCELED:
      case NotificationTypeMap.PURCHASER_ORDER_ITEM_CANCELED:
      /*if (splitted[3] === orderId) {
        fetchOrder(orderId, 'purchase');
      }
      break;*/
      case NotificationTypeMap.TRAVELER_RESERVATION_EXPIRED:
      case NotificationTypeMap.TRAVELER_ORDER_IN_QEMPO:
      case NotificationTypeMap.TRAVELER_ORDER_SHIPPING:
      case NotificationTypeMap.TRAVELER_ORDER_DISBURSEMENT_SENT:
        /*if (splitted[3] === orderId) {
          fetchOrder(orderId, 'sale');
        }*/
        break;
    }
  }

  updateCurrentViewFromClick(notification: Notification) {
    const { location, fetchOrder } = this.props;
    const splitted = location.pathname.split('/');
    const orderId: string = notification.order.id;
    switch (notification.subtype) {
      //review when orders merge with transaction
      case NotificationTypeMap.PURCHASER_ORDER_PURCHASED:
      case NotificationTypeMap.PURCHASER_ORDER_PAID:
      /*if (splitted[3] === orderId) {
        fetchOrder(orderId, 'purchase');
      }
      break;*/
      case NotificationTypeMap.PURCHASER_ORDER_SHIPPING:
      case NotificationTypeMap.PURCHASER_ORDER_ARRIVED:
      case NotificationTypeMap.PURCHASER_ORDER_DELIVERED:
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED:
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED_WITH_CANCELLATIONS:
      case NotificationTypeMap.PURCHASER_ORDER_IN_QEMPO_CONFIRMED:
      case NotificationTypeMap.PURCHASER_ORDER_RETURNED:
      case NotificationTypeMap.PURCHASER_ORDER_CANCELED:
      case NotificationTypeMap.PURCHASER_ORDER_ITEM_CANCELED:
        //fetchOrder(orderId, 'purchase');
        break;
      case NotificationTypeMap.TRAVELER_RESERVATION_EXPIRED:
      case NotificationTypeMap.TRAVELER_ORDER_IN_QEMPO:
      case NotificationTypeMap.TRAVELER_ORDER_SHIPPING:
      case NotificationTypeMap.TRAVELER_ORDER_DISBURSEMENT_SENT:
        //fetchOrder(orderId, 'sale');
        break;
    }
  }

  handleClickNotification(notification: Notification) {
    const { closeNotificationsMenu, readNotification } = this.props;
    if (notification.group) {
      notification.group.filter((n: Notification) => !n.seen)
        .forEach((n: Notification) => readNotification(n.id));
    }
    if (!notification.read) readNotification(notification.id);
    this.updateCurrentViewFromClick(notification);
    closeNotificationsMenu();
  }

  handleClickHideNotification(notification: Notification) {
    const { hideNotification } = this.props;
    if (notification.group) {
      notification.group.forEach((n: Notification) => hideNotification(n.id));
    }
    hideNotification(notification.id);
  }

  handleClickOutsideResponsive() {
    const { closeNotificationsMenu } = this.props;
    closeNotificationsMenu();
  }

  buildNotificationData(notification: Notification): { [key: string]: string } {
    const { subtype, transmitter, receiver, order } = notification;
    const transmitterFirstFirstName = transmitter && transmitter.firstName ?
      transmitter.firstName.split(' ')[0] : '';
    const receiverFirstFirstName = receiver.firstName ?
      receiver.firstName.split(' ')[0] : receiver.firstName;
    switch (subtype) {
      case NotificationTypeMap.MESSAGE_NEW:
        const orderType = order.purchaser.id === receiver.id ? 'purchase' : 'sale';
        return {
          path: `/${orderType}/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.newMessage)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_PAID:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderPaid)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_PURCHASED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${transmitterFirstFirstName} ${this.context.intl.formatMessage(messages.orderPurchased)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${transmitterFirstFirstName} ${this.context.intl.formatMessage(messages.orderReceived)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_CANCELED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderCanceled)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_ITEM_CANCELED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderItemCanceled)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_RECEIVED_WITH_CANCELLATIONS:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${transmitterFirstFirstName} ${this.context.intl.formatMessage(messages.orderReceivedWithCancellation)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_ARRIVED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${transmitterFirstFirstName} ${this.context.intl.formatMessage(messages.orderArrived)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_IN_QEMPO_CONFIRMED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderInQempoConfirmed)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_SHIPPING:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderShippingPurchase)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_DELIVERED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderDelivered)}`,
        };
      case NotificationTypeMap.PURCHASER_ORDER_RETURNED:
        return {
          path: `/purchase/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderReturned)}`,
        };
      case NotificationTypeMap.TRAVELER_RESERVATION_EXPIRED:
        return {
          path: `/sale/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.reservationExpired)}`,
        };
      case NotificationTypeMap.TRAVELER_ORDER_IN_QEMPO:
        return {
          path: `/sale/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderInQempo)}`,
        };
      case NotificationTypeMap.TRAVELER_ORDER_SHIPPING:
        return {
          path: `/sale/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderShippingTraveler)} ${receiverFirstFirstName}`,
        };
      case NotificationTypeMap.TRAVELER_ORDER_DISBURSEMENT_SENT:
        return {
          path: `/sale/order/${order.id}`,
          subtitle: `${this.context.intl.formatMessage(messages.orderDisbursementSent)}`,
        };
      default:
        return { path: '', subtitle: '' };
    }
  }

  render() {
    const {
      open, notificationsState, closeNotificationsMenu,
      fetchNotifications,
    } = this.props;
    const notifications = notificationsState.notifications &&
      notificationsState.notifications.map((notification: Notification, n: number) => {
        const { path, subtitle } = this.buildNotificationData(notification);
        const shouldBeRead = !notification.read ||
          (notification.group && notification.group.reduce((acum, n) => !n.read ? acum + 1 : acum, 0)) > 0;
        return (
          <li key={n}
              className={classNames({ [style.unread]: shouldBeRead })}>
            <div className={style.textWrap}>
              <Link to={path}
                    className={classNames({ [style.unread]: shouldBeRead })}
                    onClick={() => this.handleClickNotification(notification)}>
                <h4>{notification.order.formattedName}</h4>
                <p>{subtitle}</p>
              </Link>
            </div>
            <button className={style.hide}
                    onClick={() => this.handleClickHideNotification(notification)}>
              <i className={classNames(icons.iconCloseLight)}/>
            </button>
          </li>
        )
      });
    const noResults = (!notificationsState.notifications || !notificationsState.notifications.length)
      && !notificationsState.isFetching && !notificationsState.fetchError &&
      (<FormattedMessage id="nav.notificationsMenu.noResults">{
        txt => <p className={style.noResult}>{txt}</p>
      }</FormattedMessage>);
    const loadingNoContent = notificationsState.isFetching && !notificationsState.notifications &&
      <Loading type="circle"
               className={style.loadingNoContent}/>;
    const loadingWithContent = notificationsState.isFetching && !!notificationsState.notifications &&
      <Loading type="circle"
               className={style.loadingWithContent}/>;

    const error = notificationsState.fetchError && (
      <FormattedMessage id="nav.notificationsMenu.fetchError">{
        txt => <p className={style.noResult}>{txt}</p>
      }</FormattedMessage>);

    const list = (notificationsState.isFetching || notificationsState.notifications || notificationsState.fetchError) &&
      (<div id="scrollWrap" className={style.innerScrollWrap}>
        <InfiniteScroll offset={350}
                         disabled={notificationsState.isFetching || !notificationsState.pageCanAsk}
                         elementId="scrollWrap"
                         handleCheckpoint={() => fetchNotifications()}>
          <ul>
            {notifications}
            {noResults}
            {loadingNoContent}
            {loadingWithContent}
            {error}
          </ul>
        </InfiniteScroll>
      </div>);

    const responsiveMenu = open &&
      <aside key="responsiveAside"
             className={style.notificationResponsiveMenuWrap}>
        <section className={style.notificationsContent}>
          {/*<FormattedMessage id="nav.notificationsMenu.title">{
            txt => <h3>{txt} ({notificationsState.unseen})</h3>
          }</FormattedMessage>*/}
          {list}
          <FormattedMessage id="nav.notificationsMenu.close">{
            txt => <button onClick={() => closeNotificationsMenu()}
                           className={classNames(buttonStyle.btn, style.close)}
                           type="button">{txt}</button>
          }</FormattedMessage>
        </section>
      </aside>;

    return <Fragment>
      {open && <Helmet key="helmet">
        <style type="text/css">{`
          body {
            overflow: hidden!important;
          }
        `}</style>
      </Helmet>}
      <Mobile>
        <section onClick={this.handleClickOutsideResponsive.bind(this)}
                 className={classNames(style.veil, { [style.veilOpen]: open })}/>
        {responsiveMenu}
      </Mobile>
      <Default>
        <aside className={classNames(style.notificationMenuWrap, { [style.open]: open })}>
          <section className={style.notificationsContent}>
            {/*<FormattedMessage id="nav.notificationsMenu.title">{
              txt => <h3>{txt} ({notificationsState.unseen})</h3>
            }</FormattedMessage>*/}
            {list}
          </section>
        </aside>
      </Default>
    </Fragment>;
  }
}

NotificationsMenu.propTypes = {
  open: PropTypes.bool,
  user: PropTypes.object.isRequired,
  notificationsState: PropTypes.object.isRequired,
  closeNotificationsMenu: PropTypes.func.isRequired,
  fetchUnseen: PropTypes.func.isRequired,
  fetchNotifications: PropTypes.func.isRequired,
  readNotification: PropTypes.func.isRequired,
  hideNotification: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  newRealtimeNotification: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  fetchOrder: PropTypes.func.isRequired,
};

NotificationsMenu.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/navbar/notifications/components/NotificationsMenu.jsx